import React, { useContext, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NotificationContext } from "../../context/NotificationContext";
import { useSelector } from "react-redux";
import { useTheme } from "../../context/ThemeContext";

import MainMetricsIcon from "../../images/icons/dashFull.svg";
import UsersIcon from "../../images/icons/people-3-v3-white.svg";
import AnalyticsIcon from "../../images/icons/analytics-white.svg";
import StarIcon from "../../images/icons/star-file-white.svg";
import FlagWhiteIcon from "../../images/icons/flagFull.svg";

import EpkIcon from "../../images/icons/epk.svg";
import ActorIcon from "../../images/icons/actor-icon.svg";
import BellIcon from "../../images/icons/bellEmpty.svg";
import RequestIcon from "../../images/icons/referral sign.svg";
import MessageIcon from "../../images/icons/message.svg";
import SavedIcon from "../../images/icons/save.svg";
import SettingsIcon from "../../images/icons/settings.svg";

export default function Sidebar({ role }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { messageCount, notificationCount, userInfo, clearMessageCount } = useContext(NotificationContext);
  const userId = useSelector((state) => state.user.id);

  const sideBarList = useMemo(() => {
    const TABS = (t, userId) => ({
      METRICS:     { Title: t("Main Metrics"), to: "/admindashboard/main",      icon: MainMetricsIcon },
      USERS:       { Title: t("Users"),        to: "/admindashboard/users",      icon: UsersIcon },
      ANALYTICS:   { Title: t("Analytics"),    to: "/admindashboard/analytics",  icon: AnalyticsIcon },
      ADMIN_EPKS:  { Title: t("EPKs"),         to: "/admindashboard/epks",       icon: StarIcon },
      REPORTS:     { Title: t("Reports"),      to: "/admindashboard/reports",    icon: FlagWhiteIcon },
      SUPPORTERS:  { Title: t("Supporters"),   to: "/admindashboard/supporters", icon: RequestIcon },

      EPKS:         { Title: t("EPKs"),         to: "/dashboard/epks",            icon: EpkIcon },
      EPK_ANALYTICS: { Title: t("Analytics"),  to: "/dashboard/analytics",       icon: AnalyticsIcon },
      EDIT_USER_PAGE: { Title: t("Your Profile"), to: `/user/${userId}`,          icon: ActorIcon },
      NOTIFICATIONS:  { Title: t("Notifications"), to: "/dashboard/notifications", icon: BellIcon },
      MESSAGES:    { Title: t("Messages"),     to: "/dashboard/chat",            icon: MessageIcon },
      SAVED:       { Title: t("Saved"),        to: "/dashboard/saved",           icon: SavedIcon },
      SETTINGS:    { Title: t("Settings"),     to: "/dashboard/settings",        icon: SettingsIcon },
    });

    const tabs = TABS(t, userId);
    switch (role) {
      case "Admin":  return [tabs.METRICS, tabs.USERS, tabs.ANALYTICS, tabs.ADMIN_EPKS, tabs.REPORTS, tabs.SUPPORTERS];
      default:       return [tabs.EPKS, tabs.EPK_ANALYTICS, tabs.EDIT_USER_PAGE, tabs.NOTIFICATIONS, tabs.MESSAGES, tabs.SAVED, tabs.SETTINGS];
    }
  }, [role, t, userId]);

  const NavItem = ({ item, isMobile = false }) => (
    <NavLink
      to={item.to}
      onClick={item.Title === t("Messages") ? clearMessageCount : undefined}
      className={({ isActive }) => `
        tw-group tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-transition-all tw-no-underline
        ${isMobile ? "tw-flex-1 tw-py-5" : "tw-w-full tw-py-2"}
        ${isActive ? "tw-bg-[#712CB0] tw-rounded-xl tw-text-white" : "tw-text-white/70"}
      `}
    >
      {({ isActive }) => (
        <>
          <img
            src={item.icon}
            alt={item.Title}
            className={`tw-h-6 tw-w-6 tw-brightness-0 tw-object-contain tw-flex-shrink-0 ${
              isActive || !isLight ? 'tw-invert' : ''
            }`}
          />
          {!isMobile && (
            <p className={`tw-text-xs tw-mt-1 tw-m-0 ${isActive ? "tw-text-white" : "tw-text-white/60"}`}>
              {item.Title}
            </p>
          )}

          {item.Title === t("Messages") && messageCount > 0 && userInfo === userId && (
            <div className={`tw-absolute tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold
              ${isMobile ? "tw-right-1 tw-top-1.5 tw-h-4 tw-w-4 tw-text-[8px]" : "tw-right-2 tw-top-1 tw-h-5 tw-w-5 tw-text-[10px]"}
            `}>
              {messageCount > 9 ? "9+" : messageCount}
            </div>
          )}
          {item.Title === t("Notifications") && notificationCount > 0 && userInfo === userId && (
            <div className={`tw-absolute tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold
              ${isMobile ? "tw-right-1 tw-top-1.5 tw-h-4 tw-w-4 tw-text-[8px]" : "tw-right-2 tw-top-1 tw-h-5 tw-w-5 tw-text-[10px]"}
            `}>
              {notificationCount > 9 ? "9+" : notificationCount}
            </div>
          )}
        </>
      )}
    </NavLink>
  );

  const renderNavItems = (isMobile) =>
    sideBarList.map((item, index) => <NavItem key={index} item={item} isMobile={isMobile} />);

  return (
    <>
      {/* Desktop Sidebar */}
      <nav className="tw-hidden md:tw-flex tw-h-full tw-w-20 tw-flex-col tw-items-stretch tw-rounded-lg tw-border-r tw-border-white/10 tw-py-2 tw-gap-1 tw-bg-[#280D41]">
        {renderNavItems(false)}
      </nav>

      {/* Mobile Bottom Bar */}
      <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-flex tw-w-full tw-border-t tw-border-white/10 md:tw-hidden tw-bg-[#280D41]">
        <nav className="tw-flex tw-w-full">
          {renderNavItems(true)}
        </nav>
      </div>
    </>
  );
}
